
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, ReactNode } from "react";
import Header from "@/components/Header";
import { useIsMobile } from "@/hooks/use-mobile";
import PasswordChangeGuard from "@/components/auth/PasswordChangeGuard";

interface AlertsAuthGuardProps {
  children: ReactNode;
}

const AlertsAuthGuard = ({ children }: AlertsAuthGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const isMobile = useIsMobile();

  console.log(`🚀 [AUTH_GUARD] Auth state - loading: ${authLoading}, isAuthenticated: ${isAuthenticated}, user:`, user);

  // Check authentication only - allow access regardless of subscription
  useEffect(() => {
    console.log(`🔐 [AUTH_CHECK] useEffect triggered - authLoading: ${authLoading}, isAuthenticated: ${isAuthenticated}`);
    
    if (!authLoading && !isAuthenticated) {
      console.log(`🔐 [AUTH_CHECK] User not authenticated, redirecting to login`);
      navigate('/login', { state: { from: location.pathname + location.search + location.hash } });
      return;
    }
    
    if (!authLoading && isAuthenticated && user) {
      console.log(`✅ [AUTH_CHECK] User is authenticated, allowing access regardless of subscription`);
    }
  }, [authLoading, isAuthenticated, user, navigate, location]);

  // Show loading while auth is being checked
  if (authLoading) {
    console.log(`⏳ [RENDER] Showing auth loading state`);
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className={`container mx-auto px-4 py-8 ${isMobile ? 'max-w-full px-3' : 'max-w-4xl'}`}>
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated || !user) {
    console.log(`🚫 [RENDER] Not authenticated or no user, returning null`);
    return null;
  }

  return (
    <PasswordChangeGuard>
      {children}
    </PasswordChangeGuard>
  );
};

export default AlertsAuthGuard;
